import React, { useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface LineChartProps {
  data: {
    timestamp: number;
    value: number;
  }[];
  color?: string;
  gradientColors?: [string, string];
  height?: number;
  formatValue?: (value: number) => string;
  formatTimestamp?: (timestamp: number) => string;
  showArea?: boolean;
  showPoints?: boolean;
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  color = '#3b82f6',
  gradientColors,
  height = 128,
  formatValue = (value) => value.toFixed(2),
  formatTimestamp = (timestamp) => new Date(timestamp).toLocaleDateString(),
  showArea = true,
  showPoints = false,
}) => {
  const chartRef = useRef<ChartJS<'line'>>(null);

  // Cleanup chart on unmount
  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);
  if (!data || data.length === 0) {
    return (
      <div 
        className="flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-lg"
        style={{ height: `${height}px` }}
      >
        <span className="text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark text-sm">
          No chart data available
        </span>
      </div>
    );
  }

  const chartData = {
    labels: data.map(d => formatTimestamp(d.timestamp)),
    datasets: [
      {
        data: data.map(d => d.value),
        borderColor: color,
        backgroundColor: gradientColors ? 
          (ctx: any) => {
            const chart = ctx.chart;
            const { ctx: canvasCtx, chartArea } = chart;
            if (!chartArea) return color;
            
            const gradient = canvasCtx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, gradientColors[0]);
            gradient.addColorStop(1, gradientColors[1]);
            return gradient;
          } : 
          `${color}20`,
        borderWidth: 2,
        fill: showArea,
        pointRadius: showPoints ? 3 : 0,
        pointHoverRadius: 5,
        tension: 0.3,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: color,
        borderWidth: 1,
        callbacks: {
          label: (context: any) => formatValue(context.parsed.y),
        },
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
  };

  return (
    <div style={{ height: `${height}px` }}>
      <Line 
        ref={chartRef}
        data={chartData} 
        options={options}
        key={`line-chart-${Math.random()}`}
      />
    </div>
  );
};

export default LineChart;